import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { NOTIFICATION_DURATION_IN_MS } from '@carto/meridian-ds/theme'
import { useAppDispatch } from 'app/state'
import { queryParamToBoolean, queryParamToEnum, queryParamToNumber } from 'features/common/utils/searchParamsUtils'
import { AlertSeverity, showNotification, Notification } from 'features/notifications/state/notificationSlice'

/**
 * Consume `notify.*` query/search params and if present dispatch `showNotification`.
 *
 * All `notify.*` query/search params are removed when consumed.
 */
export function useNotificationQueryParams() {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    let searchParamsModified = false

    const notification: Notification = {
      msg: '',
      type: AlertSeverity.Info,
      formatted: true
    }

    function consumeQueryParam(name: string, consumer: (value: string) => void) {
      if (searchParams.has(name)) {
        const value = searchParams.get(name)
        if (value !== null) {
          consumer(value)
        }
        searchParams.delete(name)
        searchParamsModified = true
      }
    }

    consumeQueryParam('notify.type', (value) => {
      notification.type = queryParamToEnum(value, AlertSeverity.Info, AlertSeverity)
    })

    consumeQueryParam('notify.msg', (value) => {
      notification.msg = value
    })

    consumeQueryParam('notify.detailsMsg', (value) => {
      notification.detailsMsg = value
    })

    consumeQueryParam('notify.formatted', (value) => {
      notification.formatted = queryParamToBoolean(value)
    })

    consumeQueryParam('notify.timeout', (value) => {
      notification.timeout = queryParamToNumber(value, NOTIFICATION_DURATION_IN_MS)
    })

    consumeQueryParam('notify.closeable', (value) => {
      notification.closeable = queryParamToBoolean(value)
    })

    if (searchParamsModified) {
      setSearchParams(searchParams)
    }

    if (notification.msg) {
      dispatch(showNotification(notification))
    }
  }, [searchParams, setSearchParams, dispatch])
}
